import Layout from '@/components/Layout';
import { api, site } from '@/services/api';
import { FeaturedNewsApi, NavBarContentData } from '@/shared/types/home.types';
import { GetServerSideProps } from 'next';
import { TechnicalSpecsProps } from '@/shared/types/product.types';
import WrapperContent from '@/components/Layout/WrapperContent';
import LazyLoad from 'react-lazyload';
import dynamic from 'next/dynamic';
import Header from '@/components/Product/Header';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { mglGray } from '@/styles/designSystemColors';

const PriceHistoryChart = dynamic(() => import('@/components/Product/PriceChart'), { ssr: false });
const DealsBox = dynamic(() => import('@/components/Layout/DealsBox'), { ssr: false });
const Specifications = dynamic(() => import('@/components/Product/Specifications'), { ssr: false });
const FeaturedLastNews = dynamic(() => import('@/components/Home/FeaturedLastNews'), { ssr: false });

const HighlightAnalysis = dynamic(() => import('@/components/Product/HighlightAnalysis'));
const VideoSliders = dynamic(() => import('@/components/Home/FeaturedVideos'));
const FeaturedProducts = dynamic(() => import('@/components/Home/FeaturedProducts'));

export default function TechnicalSpecs({ navBarData, tagsData, responseData, marca, produto }: TechnicalSpecsProps): JSX.Element {
  const { titulo, empresa, videosRelacionados, analise, fichaTecnica, produtosEmpresa, melhoresPrecos, historicoPrecos } = responseData.data;

  return (
    <Layout layoutData={{ tagsData, navBarData, schema: responseData.schema }}>
      <Header dataHeader={responseData} />
      {analise?.video || (videosRelacionados && videosRelacionados.length > 0) ? (
        <WrapperContent backgroundColor={mglGray.mglGray50} paddingOnBottom={false}>
          {analise?.video && (
            <>
              <span id="analise" />
              <HighlightAnalysis title={titulo} video={{ id: analise.video.idYoutube, title: `Vídeo-análise - ${analise.video.titulo}` }} />
            </>
          )}
          {videosRelacionados && videosRelacionados.length > 0 && (
            <VideoSliders featuredVideosData={videosRelacionados} isOnProduct setBackgroundColor={mglGray.mglGray50} useTitle />
          )}
        </WrapperContent>
      ) : null}
      {melhoresPrecos && melhoresPrecos.length > 0 && (
        <LazyLoad>
          <span id="precos" />
          <DealsBox key={titulo} link="" produto={titulo} deals={melhoresPrecos} isProduct />
        </LazyLoad>
      )}
      {historicoPrecos && historicoPrecos.historico && historicoPrecos.historico.length > 0 && <PriceHistoryChart historyData={historicoPrecos} />}
      {fichaTecnica && (
        <LazyLoad>
          <span id="ficha-tecnica" />
          <Specifications fichaTecnica={fichaTecnica} />
        </LazyLoad>
      )}
      {produtosEmpresa && produtosEmpresa.length > 0 && (
        <LazyLoad>
          <FeaturedProducts
            featuredProductsData={produtosEmpresa}
            title={`Produtos ${empresa || 'relacionados'}`}
            bgColor={mglGray.mglGray200}
            indexComponent="produtosEmpresa"
          />
        </LazyLoad>
      )}
      <LazyLoad height={1}>
        <span id="noticias" />
        <FeaturedLastNews
          featuredLastNewsData={null}
          rootPage={`produto/${marca}/${produto}`}
          textTitle={`Últimas sobre ${titulo}`}
          headingTag="h2"
          noPagination
          route={`produto/${marca}/${produto}`}
          routeType="produto"
        />
      </LazyLoad>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { marca, produto } = context.query;
  const { host } = context.req.headers;

  const mainReq = { promise: site.get<FeaturedNewsApi>(`/produto/${marca}/${produto}/`), link: `/produto/${marca}/${produto}/` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const responseData = data;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const tagsData = responseData.ssr;

  context.res.setHeader('Cache-Control', responseData.headers['Cache-control']);
  context.res.setHeader('Expires', responseData.headers.Expires);
  context.res.statusCode = statusCode;
  return {
    props: {
      tagsData,
      responseData,
      navBarData,
      marca,
      produto,
    },
  };
};
